import React from "react";
import { useApp } from "@/context/AppContext";
import { Mail, Phone, MapPin, Facebook, Linkedin, Instagram, Youtube } from "lucide-react";
import { SOCIAL, CONTACT } from "@/config/links";
import { externalLinkHandler, externalTarget } from "@/utils/openExternal";

// Custom X (Twitter) glyph
const XIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Google Maps pin logo
const GoogleMapsIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path fill="currentColor" d="M12 2C7.6 2 4 5.6 4 10c0 5.25 8 12 8 12s8-6.75 8-12c0-4.4-3.6-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
  </svg>
);

export default function Footer() {
  const { t } = useApp();

  const socials = [
    { key: "facebook", label: "Facebook", href: SOCIAL.facebook, Icon: Facebook },
    { key: "instagram", label: "Instagram", href: SOCIAL.instagram, Icon: Instagram },
    { key: "youtube", label: "YouTube", href: SOCIAL.youtube, Icon: Youtube },
    { key: "linkedin", label: "LinkedIn", href: SOCIAL.linkedin, Icon: Linkedin },
    { key: "x", label: "X (Twitter)", href: SOCIAL.x, Icon: XIcon },
    { key: "gmaps", label: "Google Maps", href: SOCIAL.googleMaps, Icon: GoogleMapsIcon },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="border-t"
      style={{ borderColor: "var(--ht-border)", backgroundColor: "var(--ht-surface)" }}
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8">
        {/* Brand */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_homeatope-academy/artifacts/0u9eykli_Logotipo%20Homeatope%20volando.jpg"
              alt="Home a tope"
              className="h-14 w-14 rounded-lg object-contain"
              style={{ background: "#fff" }}
              data-testid="footer-logo-img"
            />
            <span className="ht-wordmark" translate="no">
              <span className="ht-wordmark-home text-xl" translate="no">HOME</span>
              <span className="ht-wordmark-atope text-lg" translate="no">a tope</span>
              <span
                className="mt-1 text-[10px] font-semibold uppercase tracking-[0.22em]"
                style={{ color: "var(--ht-text-muted)" }}
              >
                Fontanería · Electricidad
              </span>
            </span>
          </div>
          <p className="mt-4 text-sm" style={{ color: "var(--ht-text-muted)" }}>
            {t.footer.tagline}
          </p>

          <div className="mt-6 space-y-2 text-sm" style={{ color: "var(--ht-text)" }}>
            <div className="flex items-center gap-2">
              <Phone size={14} style={{ color: "var(--ht-primary)" }} />
              <a
                href={`tel:${CONTACT.phoneTel}`}
                onClick={externalLinkHandler(`tel:${CONTACT.phoneTel}`)}
                data-testid="footer-phone"
              >
                {CONTACT.phoneDisplay}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={14} style={{ color: "var(--ht-primary)" }} />
              <a
                href={`mailto:${CONTACT.email}`}
                onClick={externalLinkHandler(`mailto:${CONTACT.email}`)}
                data-testid="footer-email"
              >
                {CONTACT.email}
              </a>
            </div>
            <div className="flex items-center gap-2" style={{ color: "var(--ht-text-muted)" }}>
              <MapPin size={14} style={{ color: "var(--ht-primary)" }} />
              Santiago de Compostela · Comarca · Oroso
            </div>
          </div>
        </div>

        {/* Nav */}
        <div className="lg:col-span-3">
          <h4
            className="text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ color: "var(--ht-text-muted)" }}
          >
            {t.nav.services}
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            {t.services.items.map((s) => (
              <li key={s.key}>
                <a
                  href="#servicios"
                  data-testid={`footer-link-${s.key}`}
                  style={{ color: "var(--ht-text)" }}
                  className="hover:opacity-70"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Social */}
        <div className="lg:col-span-4">
          <h4
            className="text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ color: "var(--ht-text-muted)" }}
          >
            {t.footer.followUs}
          </h4>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="footer-socials">
            {socials.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target={externalTarget()}
                rel="noopener noreferrer"
                onClick={externalLinkHandler(s.href)}
                aria-label={s.label}
                data-testid={`footer-social-${s.key}`}
                className="ht-card flex h-10 w-10 items-center justify-center transition-transform hover:-translate-y-0.5"
                style={{ color: "var(--ht-primary)" }}
              >
                <s.Icon size={16} />
              </a>
            ))}
          </div>

          <p
            className="mt-6 text-xs leading-relaxed"
            style={{ color: "var(--ht-text-muted)" }}
          >
            {t.footer.tagline2}
          </p>
        </div>
      </div>

      <div className="border-t" style={{ borderColor: "var(--ht-border)" }}>
        <div
          className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-4 py-5 text-xs sm:flex-row sm:items-center sm:px-6 lg:px-8"
          style={{ color: "var(--ht-text-muted)" }}
        >
          <span data-testid="footer-rights">
            © {new Date().getFullYear()} Home a tope · {t.footer.rights}
          </span>
          <div className="flex gap-4">
            <a href="#" className="hover:opacity-70" data-testid="footer-legal">
              {t.footer.legal}
            </a>
            <a href="#" className="hover:opacity-70" data-testid="footer-privacy">
              {t.footer.privacy}
            </a>
            <a href="#" className="hover:opacity-70" data-testid="footer-cookies">
              {t.footer.cookies}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
